import * as React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { ToolButtonContext } from './ToolButton';

interface Props extends React.SVGProps<SVGSVGElement> {
  size?: number;
  fillRatio?: number;
  isBooped?: boolean;
  includePivotPoints?: boolean;
  mainPivotColor?: string;
  fusePivotColor?: string;
  boopRotation?: number;
}

function IconBomb({
  size = 24,
  fillRatio = 0,
  isBooped = false,
  boopRotation = 40,
  includePivotPoints = false,
  mainPivotColor = 'hsl(180deg 100% 40%)',
  fusePivotColor = 'hsl(350deg 100% 50%)',
  ...props
}: Props) {
  const spring = useSpring({
    transform: `rotate(${isBooped ? boopRotation : 0}deg)`,
    transformOrigin: '75% 25%',
    config: {
      tension: 400,
      friction: 10,
    },
  });

  const uniqueId = React.useId().replace(/:/g, '');
  const maskId = `mask-${uniqueId}`;

  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill="none"
      viewBox="0 0 24 24"
      {...props}
    >
      <g mask={includePivotPoints ? `url(#${maskId})` : undefined}>
        <path
          fill="currentColor"
          d="m20.468 1.037-.466 2.96 2.96-.468-2.96.472 1.361 2.671L20 4.003 17 7l2.998-3-2.67-1.36 2.671 1.357z"
        />
        <animated.path
          fill="currentColor"
          style={spring}
          d="m20.468 1.037.988.155A1 1 0 0 0 19.48.88zm-.466 2.96-.988-.154a1 1 0 0 0 1.144 1.143zm2.96-.468.158.988a1 1 0 0 0-.314-1.975zm-2.96.472-.157-.987a1 1 0 0 0-.733 1.441zm1.361 2.671-.89.455a1 1 0 0 0 1.781-.909zM20 4.003l.89-.455a1 1 0 0 0-1.596-.252zM17 7l-.707-.707a1 1 0 0 0 1.415 1.414zm2.998-3 .707.707a1 1 0 0 0-.253-1.597zm-2.67-1.36.452-.892a1 1 0 0 0-.907 1.782zm2.671 1.357-.453.892a1 1 0 0 0 1.441-.735zM19.48.881l-.466 2.962 1.976.31.466-2.96zm.678 4.105 2.961-.47-.313-1.974-2.96.468zm2.647-2.444-2.96.472.315 1.975 2.96-.472zm-3.693 1.913 1.36 2.671 1.782-.908-1.36-2.67zm3.142 1.762L20.89 3.548l-1.781.91 1.364 2.67zm-2.961-2.921-3 2.997 1.414 1.414 3-2.997zm-1.586 4.41 2.997-2.999-1.415-1.413-2.997 3zm2.744-4.596-2.67-1.362-.909 1.782 2.67 1.361zm-3.578.42 2.672 1.359.906-1.783-2.672-1.358zm4.113.624.47-2.96L19.48.88l-.47 2.96z"
        />

        <path
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2"
          d="M13.444 8.347A8 8 0 1 0 15.93 11"
        />
        <path
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="m12.359 7.649 1.06-1.061a2 2 0 0 1 2.829 0l1.414 1.414a2 2 0 0 1 0 2.829l-1.06 1.06"
        />
      </g>
      <mask id={maskId}>
        <rect width="100%" height="100%" fill="white" />
        <circle cx="18" cy="6" r="2" fill="black" />
      </mask>
      {includePivotPoints && (
        <>
          <circle cx="18" cy="6" r="1.25" fill={fusePivotColor} />
          <circle
            cx={24 * 0.375}
            cy={24 * 0.625}
            r="2"
            fill={mainPivotColor}
          />
        </>
      )}
    </Svg>
  );
}

export function ConnectedIconBomb({
  size = 24,
  fillRatio = 0,
  ...props
}: Props) {
  const isBooped = React.use(ToolButtonContext);

  return (
    <IconBomb
      size={size}
      fillRatio={fillRatio}
      isBooped={isBooped}
      {...props}
    />
  );
}

const Svg = styled.svg`
  position: relative;
`;

export default IconBomb;
